// src/components/common/AwardCompOffModal.js
import React, { useState } from 'react';
import { X, Award } from 'lucide-react';
import { useLeave } from '../../contexts/LeaveContext';

const AwardCompOffModal = ({ isOpen, onClose, profile }) => {
    const { awardCompensatoryOff } = useLeave();
    const [formData, setFormData] = useState({
        workDate: '',
        days: 1,
        reason: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!formData.workDate || !formData.reason) {
            setError('Please fill all required fields');
            return;
        }

        // Include employee ID in the request
        const compOffRequest = {
            employeeId: profile?.id,
            workDate: formData.workDate,
            days: parseFloat(formData.days),
            reason: formData.reason
        };

        const result = await awardCompensatoryOff(compOffRequest);
        if (result.success) {
            setSuccess(result.message);
            setTimeout(() => {
                onClose();
                setFormData({ workDate: '', days: 1, reason: '' });
                setSuccess('');
                setError('');
            }, 1500);
        } else {
            setError(result.message);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50 p-6 max-w-2xl w-full">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                        <Award className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Award Compensatory Off</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                        Award compensatory off to <strong>{profile?.first_name} {profile?.last_name}</strong> for working on holidays or weekends. The credited leave will be added to their leave balance.
                    </p>
                </div>

                {error && (
                    <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                        <p className="text-sm text-green-700 dark:text-green-400">{success}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Work Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            name="workDate"
                            value={formData.workDate}
                            onChange={handleChange}
                            max={new Date().toISOString().split('T')[0]}
                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                            required
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Select the date you worked (holiday/weekend)
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Number of Days <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="days"
                            value={formData.days}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                            required
                        >
                            <option value="0.5">Half Day (0.5)</option>
                            <option value="1">Full Day (1)</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Reason / Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            name="reason"
                            value={formData.reason}
                            onChange={handleChange}
                            rows="4"
                            placeholder="Describe the work performed on this day..."
                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white resize-none"
                            required
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                        >
                            <Award className="w-4 h-4" />
                            Award Comp Off
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default AwardCompOffModal;
